<template>
  <div class="app-container">
    <!-- Header Section -->
    <div class="header-section">
      <h2 class="page-title">Prośby o dedykowane integracje</h2>
      <p class="page-subtitle">Zarządzaj prośbami użytkowników o integracje z ich systemami CRM</p>
    </div>

    <!-- Statistics Cards -->
    <el-row :gutter="20" class="stats-cards">
      <el-col :xs="24" :sm="8">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-number">{{ stats.total }}</div>
            <div class="stat-label">Wszystkie prośby</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="8">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-number">{{ stats.thisWeek }}</div>
            <div class="stat-label">Prośby w tym tygodniu</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="8">
        <el-card class="stat-card">
          <div class="stat-content">
            <div class="stat-number">{{ stats.thisMonth }}</div>
            <div class="stat-label">Prośby w tym miesiącu</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Requests Table -->
    <el-card class="requests-card">
      <div slot="header" class="card-header">
        <h3>Lista próśb o integracje</h3>
        <p>Przeglądaj i zarządzaj prośbami użytkowników o dedykowane integracje CRM</p>
      </div>

      <el-table 
        v-loading="loading" 
        :data="requests" 
        border 
        style="width: 100%"
        :default-sort="{prop: 'created_at', order: 'descending'}"
      >
        <el-table-column label="Imię / Firma" min-width="180">
          <template slot-scope="scope">
            <div class="user-cell">
              <div v-if="scope.row.name" class="user-name">{{ scope.row.name }}</div>
              <div v-if="scope.row.company" class="user-company">{{ scope.row.company }}</div>
              <div v-if="!scope.row.name && !scope.row.company" class="text-gray">—</div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Użytkownik" min-width="200">
          <template slot-scope="scope">
            <div v-if="hasUser(scope.row.user)" class="user-cell">
              <div class="user-name">{{ getUserName(scope.row.user) }}</div>
              <div class="user-email">{{ getUserEmail(scope.row.user) }}</div>
              <div v-if="getUserCompanyName(scope.row.user)" class="user-company">
                {{ getUserCompanyName(scope.row.user) }}
              </div>
            </div>
            <el-tag v-else type="warning" size="small">Landing page</el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Typ integracji" width="180" align="center">
          <template slot-scope="scope">
            <el-tag :type="integrationTypeTag(scope.row.integration_type)" size="small">
              {{ integrationTypeLabel(scope.row.integration_type) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="System CRM" width="180">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.crm_system_name" type="info" size="small">
              {{ scope.row.crm_system_name }}
            </el-tag>
            <span v-else class="text-gray">Nie podano</span>
          </template>
        </el-table-column>

        <el-table-column label="Email kontaktowy" width="220">
          <template slot-scope="scope">
            <div class="contact-info">
              <i class="el-icon-message"></i>
              <a :href="'mailto:' + scope.row.contact_email" class="email-link">
                {{ scope.row.contact_email }}
              </a>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Data zgłoszenia" width="150" align="center" sortable prop="created_at">
          <template slot-scope="scope">
            {{ formatDate(scope.row.created_at) }}
          </template>
        </el-table-column>

        <el-table-column label="Akcje" width="150" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button 
              type="primary" 
              size="small" 
              icon="el-icon-view"
              @click="handleViewDetails(scope.row)"
            >
              Szczegóły
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="requests.length === 0 && !loading" class="empty-state">
        <i class="el-icon-folder-opened"></i>
        <p>Brak próśb o integracje</p>
      </div>
    </el-card>

    <!-- View Details Dialog -->
    <el-dialog 
      :visible.sync="detailsVisible" 
      title="Szczegóły prośby o integrację"
      width="700px"
      custom-class="details-dialog"
    >
      <div v-if="selectedRequest" class="details-content">
        <el-descriptions :column="2" border>
          <el-descriptions-item v-if="selectedRequest.name || selectedRequest.company" label="Dane z formularza" :span="2">
            <div class="user-info">
              <strong v-if="selectedRequest.name">{{ selectedRequest.name }}</strong>
              <span v-if="selectedRequest.company" class="user-company-detail">Firma: {{ selectedRequest.company }}</span>
            </div>
          </el-descriptions-item>

          <el-descriptions-item label="Użytkownik" :span="2">
            <div v-if="hasUser(selectedRequest.user)" class="user-info">
              <strong>{{ getUserName(selectedRequest.user) }}</strong>
              <span class="user-email-detail">{{ getUserEmail(selectedRequest.user) }}</span>
              <span v-if="getUserCompanyName(selectedRequest.user)" class="user-company-detail">
                Firma: {{ getUserCompanyName(selectedRequest.user) }}
              </span>
            </div>
            <el-tag v-else type="warning">Niezalogowany (landing page)</el-tag>
          </el-descriptions-item>
          
          <el-descriptions-item label="Typ integracji">
            <el-tag :type="integrationTypeTag(selectedRequest.integration_type)">
              {{ integrationTypeLabel(selectedRequest.integration_type) }}
            </el-tag>
          </el-descriptions-item>
          
          <el-descriptions-item label="System CRM">
            <el-tag v-if="selectedRequest.crm_system_name" type="info">
              {{ selectedRequest.crm_system_name }}
            </el-tag>
            <span v-else class="text-gray">Nie podano</span>
          </el-descriptions-item>
          
          <el-descriptions-item label="Email kontaktowy">
            <a :href="'mailto:' + selectedRequest.contact_email" class="email-link-detail">
              <i class="el-icon-message"></i>
              {{ selectedRequest.contact_email }}
            </a>
          </el-descriptions-item>
          
          <el-descriptions-item v-if="selectedRequest.sync_frequency" label="Częstotliwość synchronizacji">
            {{ getFrequencyLabel(selectedRequest.sync_frequency) }}
          </el-descriptions-item>
          
          <el-descriptions-item v-if="selectedRequest.data_size" label="Wielkość danych">
            {{ getDataSizeLabel(selectedRequest.data_size) }}
          </el-descriptions-item>
          
          <el-descriptions-item v-if="selectedRequest.security_level" label="Poziom bezpieczeństwa" :span="2">
            <el-tag :type="getSecurityTagType(selectedRequest.security_level)">
              {{ getSecurityLabel(selectedRequest.security_level) }}
            </el-tag>
          </el-descriptions-item>
          
          <el-descriptions-item label="Data zgłoszenia">
            {{ formatDate(selectedRequest.created_at) }}
          </el-descriptions-item>
          
          <el-descriptions-item label="Ostatnia aktualizacja">
            {{ formatDate(selectedRequest.updated_at) }}
          </el-descriptions-item>
          
          <el-descriptions-item v-if="selectedRequest.additional_notes" label="Dodatkowe uwagi" :span="2">
            <div class="additional-notes">
              {{ selectedRequest.additional_notes }}
            </div>
          </el-descriptions-item>
        </el-descriptions>

        <div class="action-section">
          <el-divider>Kontakt z użytkownikiem</el-divider>
          <div class="contact-actions">
            <el-button 
              type="success" 
              icon="el-icon-message"
              @click="openEmailClient(selectedRequest)"
            >
              Wyślij wiadomość email
            </el-button>
            <el-button 
              type="info" 
              icon="el-icon-phone"
              v-if="getUserPhone(selectedRequest.user)"
            >
              Zadzwoń: {{ getUserPhone(selectedRequest.user) }}
            </el-button>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import IntegrationResource from '@/api/integration';

const integrationResource = new IntegrationResource();

export default {
  name: 'IntegrationRequests',
  data() {
    return {
      loading: false,
      requests: [],
      stats: {
        total: 0,
        thisWeek: 0,
        thisMonth: 0,
      },
      detailsVisible: false,
      selectedRequest: null,
    };
  },
  mounted() {
    this.loadRequests();
  },
  methods: {
    hasUser(user) {
      return !!user && typeof user === 'object';
    },

    getUserName(user) {
      return this.hasUser(user) ? (user.name || '—') : '—';
    },

    getUserEmail(user) {
      return this.hasUser(user) ? (user.email || '—') : '—';
    },

    getUserCompanyName(user) {
      return this.hasUser(user) ? (user.company_name || '') : '';
    },

    getUserPhone(user) {
      return this.hasUser(user) ? (user.phone || '') : '';
    },

    async loadRequests() {
      this.loading = true;
      try {
        console.log('Attempting to load integration requests...');
        const response = await integrationResource.getCustomIntegrationRequests();
        console.log('Integration requests response:', response);
        console.log('Response data:', response.data);
        // The response IS the data array, not wrapped in response.data
        this.requests = Array.isArray(response) ? response : (response.data || []);
        console.log('Loaded requests:', this.requests);
        this.calculateStats();
      } catch (error) {
        console.error('Error loading integration requests:', error);
        console.error('Error response:', error.response);
        console.error('Error message:', error.message);
        const errorMsg = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : error.message;
        this.$message.error('Błąd podczas ładowania próśb o integracje: ' + errorMsg);
      } finally {
        this.loading = false;
      }
    },

    calculateStats() {
      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      this.stats.total = this.requests.length;
      this.stats.thisWeek = this.requests.filter(r => new Date(r.created_at) >= oneWeekAgo).length;
      this.stats.thisMonth = this.requests.filter(r => new Date(r.created_at) >= oneMonthAgo).length;
    },

    handleViewDetails(request) {
      this.selectedRequest = request;
      this.detailsVisible = true;
    },

    openEmailClient(request) {
      const userName = (request.user && request.user.name) || request.name || '';
      const subject = encodeURIComponent(`Dazu.pl - Prośba o integrację CRM (${request.crm_system_name || 'System CRM'})`);
      const body = encodeURIComponent(`Dzień dobry ${userName},\n\nDziękujemy za zainteresowanie integracją Dazu.pl z Państwa systemem CRM.\n\n`);
      window.open(`mailto:${request.contact_email}?subject=${subject}&body=${body}`, '_blank');
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('pl-PL', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getFrequencyLabel(frequency) {
      const labels = {
        'realtime': 'W czasie rzeczywistym',
        'hourly': 'Co godzinę',
        'every_6_hours': 'Co 6 godzin',
        'daily': 'Codziennie',
        'weekly': 'Co tydzień',
      };
      return labels[frequency] || 'Nie określono';
    },

    getDataSizeLabel(size) {
      const labels = {
        'small': '< 100 MB',
        'medium': '100 MB - 1 GB',
        'large': '1 GB - 10 GB',
        'xlarge': '> 10 GB',
      };
      return labels[size] || 'Nie określono';
    },

    getSecurityLabel(level) {
      const labels = {
        'basic': 'Podstawowy',
        'standard': 'Standardowy',
        'high': 'Wysoki',
        'enterprise': 'Enterprise (najwyższy)',
      };
      return labels[level] || 'Nie określono';
    },

    getSecurityTagType(level) {
      const types = {
        'basic': '',
        'standard': 'info',
        'high': 'warning',
        'enterprise': 'danger',
      };
      return types[level] || '';
    },

    integrationTypeLabel(type) {
      const labels = {
        'ftp': 'FTP / XML',
        'api': 'API',
        'migracja_danych': 'Migracja danych',
        'inne': 'Inne',
      };
      return labels[type] || type;
    },

    integrationTypeTag(type) {
      const tags = {
        'ftp': '',
        'api': 'success',
        'migracja_danych': 'warning',
        'inne': 'info',
      };
      return tags[type] || '';
    },
  },
};
</script>

<style scoped lang="scss">
.app-container {
  padding: 20px;
}

.header-section {
  margin-bottom: 30px;
  text-align: center;

  .page-title {
    font-size: 28px;
    font-weight: 600;
    color: #303133;
    margin: 0 0 10px 0;
  }

  .page-subtitle {
    font-size: 14px;
    color: #909399;
    margin: 0;
  }
}

.stats-cards {
  margin-bottom: 30px;

  .stat-card {
    text-align: center;
    margin-bottom: 15px;

    .stat-content {
      padding: 10px 0;

      .stat-number {
        font-size: 36px;
        font-weight: 700;
        color: #409EFF;
        margin-bottom: 8px;
      }

      .stat-label {
        font-size: 14px;
        color: #909399;
      }
    }
  }
}

.card-header {
  h3 {
    margin: 0 0 5px 0;
    font-size: 18px;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 13px;
    color: #909399;
  }
}

.user-cell {
  .user-name {
    font-weight: 600;
    color: #303133;
    margin-bottom: 3px;
  }

  .user-email {
    font-size: 12px;
    color: #606266;
    margin-bottom: 2px;
  }

  .user-company {
    font-size: 12px;
    color: #909399;
    font-style: italic;
  }
}

.integration-type {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 600;
  font-size: 13px;

  .icon-ftp {
    width: 18px;
    height: 18px;
    color: #409EFF;
  }

  .icon-api {
    width: 18px;
    height: 18px;
    color: #67C23A;
  }

  span {
    color: #303133;
  }
}

.contact-info {
  display: flex;
  align-items: center;
  gap: 5px;

  i {
    color: #409EFF;
  }

  .email-link {
    color: #409EFF;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #909399;

  i {
    font-size: 64px;
    margin-bottom: 15px;
    display: block;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

.details-content {
  .user-info {
    display: flex;
    flex-direction: column;
    gap: 5px;

    .user-email-detail {
      font-size: 13px;
      color: #606266;
    }

    .user-company-detail {
      font-size: 12px;
      color: #909399;
      font-style: italic;
    }
  }

  .integration-type-detail {
    display: flex;
    align-items: center;
    gap: 8px;

    .icon-detail {
      width: 20px;
      height: 20px;
      color: #409EFF;
    }

    span {
      font-weight: 600;
      color: #303133;
    }
  }

  .email-link-detail {
    color: #409EFF;
    text-decoration: none;
    display: flex;
    align-items: center;
    gap: 5px;
    
    &:hover {
      text-decoration: underline;
    }

    i {
      font-size: 14px;
    }
  }

  .additional-notes {
    padding: 10px;
    background: #f5f7fa;
    border-radius: 4px;
    white-space: pre-wrap;
    font-size: 13px;
    color: #606266;
    line-height: 1.6;
  }

  .action-section {
    margin-top: 20px;

    .contact-actions {
      display: flex;
      gap: 10px;
      justify-content: center;
    }
  }
}

.text-gray {
  color: #909399;
  font-style: italic;
}
</style>
